import { Checkbox, Icon, Modal, TextField } from '@shopify/polaris';
import { LinkIcon } from '@shopify/polaris-icons';
import { useState } from 'react';

interface LinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (url: string, text: string, openInNewTab: boolean) => void;
  onFocus: () => void;
}

const LinkModal = ({ isOpen, onClose, onSubmit, onFocus }: LinkModalProps) => {
  const [url, setUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [openInNewTab, setOpenInNewTab] = useState(false);
  const [urlError, setUrlError] = useState('');
  const [linkTextError, setLinkTextError] = useState('');

  const handleSubmit = () => {
    if (!url || !linkText) {
      onClose();
      return;
    }
    onSubmit(url, linkText, openInNewTab);
    setUrl('');
    setLinkText('');
    setOpenInNewTab(false);
    onClose();
    onFocus();
  };

  const handleUrlChange = (value: string) => {
    setUrl(value);
    if (!value) setUrlError('This field is required');
    else setUrlError('');
  };

  const handleUrlBlur = () => {
    if (!url) setUrlError('This field is required');
  };

  const handleLinkTextChange = (value: string) => {
    setLinkText(value);
    if (!value) setLinkTextError('This field is required');
    else setLinkTextError('');
  };

  const handleLinkTextBlur = () => {
    if (!linkText) setLinkTextError('This field is required');
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size="small"
      title="Insert Link"
      primaryAction={{
        content: 'Insert Link',
        onAction: handleSubmit,
        disabled: !url || !linkText,
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: onClose,
        },
      ]}
    >
      <Modal.Section>
        <div className="mb-16" id="link-btn-in-editor">
          <TextField
            label="Link Text"
            value={linkText}
            onChange={handleLinkTextChange}
            autoComplete="off"
            requiredIndicator
            error={linkTextError}
            onBlur={handleLinkTextBlur}
          />
        </div>
        <div className="mb-16">
          <TextField
            label="URL"
            value={url}
            onChange={handleUrlChange}
            autoComplete="off"
            type="url"
            requiredIndicator
            error={urlError}
            onBlur={handleUrlBlur}
          />
        </div>
        <Checkbox label="Open in new tab" checked={openInNewTab} onChange={setOpenInNewTab} />
      </Modal.Section>
    </Modal>
  );
};

export const LinkButton = ({ onFocus, disabled }: { onFocus: () => void; disabled?: boolean }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [savedRange, setSavedRange] = useState<Range | null>(null);

  const handleInsertLink = (url: string, text: string, openInNewTab: boolean) => {
    const linkText = savedRange?.toString() || text;
    const target = openInNewTab ? '_blank' : '';
    const rel = openInNewTab ? 'noopener noreferrer' : '';

    // Restore the saved selection
    if (savedRange) {
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(savedRange);
    }

    // Focus the editor before inserting
    onFocus();

    // Create the link HTML
    const linkHtml = `<a href="${url}" target="${target}" rel="${rel}">${linkText}</a>`;
    document.execCommand('insertHTML', false, linkHtml);
  };

  const handleOpenModal = () => {
    // Save the current selection before opening modal
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      setSavedRange(selection.getRangeAt(0).cloneRange());
    }
    setIsModalOpen(true);
  };

  return (
    <>
      <button
        onClick={handleOpenModal}
        style={{
          padding: '4px 6px',
          backgroundColor: '#fff',
          border: '1px solid #ccc',
          borderRadius: '4px',
          cursor: 'pointer',
        }}
        title="Insert Link"
        disabled={disabled}
      >
        <Icon source={LinkIcon} tone="base" />
      </button>
      <LinkModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSubmit={handleInsertLink} onFocus={onFocus} />
    </>
  );
};
